import { apiClient } from './api';

/**
 * Booking service for calendar API interactions
 */
class BookingService {
  /**
   * Get meeting types for a client
   * @param {string} clientId - Client/customer ID
   * @returns {Promise<Object>} Meeting types configuration
   */
  async getMeetingTypes(clientId) {
    try {
      return await apiClient.get(`/api/v1/calendar/meeting-types/${clientId}`, {
        clientId,
      });
    } catch (error) {
      console.error('Error fetching meeting types:', error);
      throw error;
    }
  }

  /**
   * Search for available time slots
   * @param {string} clientId - Client/customer ID
   * @param {Object} params - Search parameters
   * @param {string} params.start_date - Start date (ISO format)
   * @param {string} params.end_date - End date (ISO format)
   * @param {string} params.meeting_type - Meeting type ID
   * @param {number} params.total_slots - Optional max slots to return
   * @returns {Promise<Object>} Available slots
   */
  async searchAvailability(clientId, params) {
    try {
      return await apiClient.get(`/api/v1/calendar/availability/${clientId}`, {
        params,
        clientId,
      });
    } catch (error) {
      console.error('Error searching availability:', error);
      throw error;
    }
  }

  /**
   * Book an appointment
   * @param {Object} bookingData - Booking data
   * @param {string} bookingData.customer_id - Customer ID
   * @param {string} bookingData.calendar_id - Calendar ID
   * @param {string} bookingData.start_time - Start time (ISO format)
   * @param {string} bookingData.end_time - End time (ISO format)
   * @param {string} bookingData.meeting_type - Meeting type
   * @param {string} bookingData.customer_name - Customer name
   * @param {string} bookingData.customer_email - Customer email
   * @param {string} bookingData.customer_phone - Customer phone
   * @param {string} bookingData.notes - Optional notes
   * @returns {Promise<Object>} Booking confirmation
   */
  async bookAppointment(bookingData) {
    try {
      return await apiClient.post('/api/v1/calendar/book', bookingData, {
        clientId: bookingData.customer_id,
      });
    } catch (error) {
      console.error('Error booking appointment:', error);
      throw error;
    }
  }

  /**
   * Reschedule an existing booking
   * @param {Object} rebookData - Rebooking data
   * @param {string} rebookData.customer_id - Customer ID
   * @param {string} rebookData.event_id - Original event ID
   * @param {string} rebookData.new_start_time - New start time (ISO format)
   * @param {string} rebookData.new_end_time - New end time (ISO format)
   * @param {string} rebookData.timezone - Timezone for validation (default: Europe/Warsaw)
   * @returns {Promise<Object>} Rebooking confirmation
   */
  async rescheduleBooking(rebookData) {
    try {
      // Add timezone if not provided
      const dataWithTimezone = {
        ...rebookData,
        timezone: rebookData.timezone || 'Europe/Warsaw'
      };
      
      return await apiClient.post('/api/v1/calendar/rebook', dataWithTimezone, {
        clientId: rebookData.customer_id,
      });
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      throw error;
    }
  }

  /**
   * Get events for a customer filtered by phone number
   * @param {string} customerId - Customer ID
   * @param {string} phoneNumber - Phone number to filter events (e.g., +48577188884)
   * @param {Object} params - Optional query parameters
   * @param {string} params.start_date - Start date (YYYY-MM-DD)
   * @param {string} params.end_date - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} Events list filtered by phone
   */
  async getUserBookings(customerId, phoneNumber, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `/api/v1/calendar/events/${customerId}/${encodeURIComponent(phoneNumber)}${queryString ? `?${queryString}` : ''}`;
      return await apiClient.get(url, {
        clientId: customerId,
      });
    } catch (error) {
      console.error('Error fetching user bookings:', error);
      throw error;
    }
  }

  /**
   * Get events for a customer
   * @param {string} customerId - Customer ID
   * @param {string} filter - Filter type ('*' for all, or phone number for specific user)
   * @returns {Promise<Object>} Events list
   */
  async getEvents(customerId, filter = '*') {
    try {
      return await apiClient.get(`/api/v1/calendar/events/${customerId}/${filter}`, {
        clientId: customerId,
      });
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  /**
   * Cancel a booking
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Calendar name
   * @param {string} eventId - Event ID to cancel
   * @param {string} timezone - Timezone for validation (default: Europe/Warsaw)
   * @returns {Promise<Object>} Cancellation confirmation
   */
  async cancelBooking(customerId, calendarName, eventId, timezone = 'Europe/Warsaw') {
    try {
      return await apiClient.post('/api/v1/calendar/delete', {
        customer_id: customerId,
        calendar_name: calendarName,
        event_id: eventId,
        timezone: timezone
      }, {
        clientId: customerId,
      });
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw error;
    }
  }

  /**
   * Get reminders configuration
   * @param {string} clientId - Client ID
   * @returns {Promise<Object>} Reminders configuration
   */
  async getReminders(clientId) {
    try {
      return await apiClient.get('/api/v1/calendar/reminders', {
        clientId,
      });
    } catch (error) {
      console.error('Error fetching reminders:', error);
      throw error;
    }
  }
}

export const bookingService = new BookingService();
export default BookingService;
